import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useRef } from "react";
import { token } from "../utils/tokens";

gsap.registerPlugin(useGSAP, ScrollTrigger);

// Page background (--page-bg, used by .App-header and the tinted controls) as
// a pure function of scroll: hero color -> `base` over the first 100px, then
// to each section's color as that section scrolls in. Computed from every
// segment's progress on each update rather than chained .to() tweens, which
// capture their start color at first render and so drift with scroll history
// (and break when the palette changes mid-page).
// sections: [{ trigger: ".selector", color: "#hex" }]
// heroColor: optional color for the top of the page (a palette the visitor
// picked). Defaults to --bg.
// A palette change crossfades from the color on screen to the new one.
export const useSectionBackgrounds = (containerRef, sections, base, enabled = true, heroColor = null) => {
    const shown = useRef(null); // color currently on screen
    useGSAP(
        () => {
            if (!enabled) return;
            const q = gsap.utils.selector(containerRef);
            const root = document.documentElement;
            const colors = [heroColor || token("--bg"), base, ...sections.map((s) => s.color)];
            const fade = { from: shown.current, p: shown.current ? 0 : 1 };
            let segments = []; // ScrollTrigger.create can fire onRefresh before this is filled

            const apply = () => {
                // Segments run top to bottom; the furthest one entered wins
                let color = colors[0];
                segments.forEach((st, i) => {
                    if (st.progress > 0) color = gsap.utils.interpolate(colors[i], colors[i + 1], st.progress);
                });
                if (fade.p < 1) color = gsap.utils.interpolate(fade.from, color, fade.p);
                root.style.setProperty("--page-bg", color);
                shown.current = color;
            };

            const segment = (trigger, start, end) =>
                ScrollTrigger.create({ trigger, start, end, onUpdate: apply, onRefresh: apply });
            segments = [
                segment(containerRef.current, "top top", "100px top"),
                ...sections.map(({ trigger }) => segment(q(trigger), "top 80%", "top 10%")),
            ];

            if (fade.p < 1) gsap.to(fade, { p: 1, duration: 0.6, ease: "power2.out", onUpdate: apply });
            apply();

            // shown is kept, so the next palette crossfades from it
            return () => root.style.removeProperty("--page-bg");
        },
        // Rebuild when the palette changes (the crossfade covers the swap)
        { scope: containerRef, dependencies: [sections, base, enabled, heroColor], revertOnUpdate: true }
    );
};

// Case studies: scrubs the page background (--page-bg) from black to the
// study's color over the first 1000px of scroll.
export const usePageBackground = (containerRef, color) =>
    useGSAP(
        () => {
            gsap.fromTo(
                document.documentElement,
                { "--page-bg": "#000000" },
                {
                    "--page-bg": color,
                    scrollTrigger: {
                        trigger: containerRef.current,
                        start: "top top",
                        end: "+=1000",
                        scrub: true,
                    },
                }
            );
        },
        { scope: containerRef }
    );
